import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { LightboxContentPrimitive, LightboxService, LightboxState } from '@core/services';
import { SvgIconComponent } from 'angular-svg-icon';

type LightboxVisualState = 'closed' | 'opening' | 'open' | 'closing';

@Component({
  selector: 'dialog[oke-lightbox]',
  imports: [NgTemplateOutlet, SvgIconComponent],
  templateUrl: './lightbox.html',
  styleUrl: './lightbox.scss',
  host: {
    '[attr.data-lightbox-state]': '$visualState()',
    '[class.is-default]': '$state().mode === "default"',
    '(cancel)': 'onCancel($event)',
    '(click)': 'onBackdropClick($event)',
    '(animationend)': 'onAnimationEnd($event)'
  }
})
export class Lightbox implements AfterViewInit, OnDestroy {
  private readonly _lightboxSvc: LightboxService = inject(LightboxService);
  private readonly _hostElement = inject<ElementRef<HTMLDialogElement>>(ElementRef);
  private readonly _$dynamicOutlet = viewChild('dynamicOutlet', { read: ViewContainerRef });
  private readonly _$visualState: WritableSignal<LightboxVisualState> = signal<LightboxVisualState>('closed');
  protected readonly $visualState: Signal<LightboxVisualState> = this._$visualState.asReadonly();
  protected readonly $state: Signal<LightboxState> = this._lightboxSvc.$state;
  protected readonly $titleContent: Signal<LightboxContentPrimitive | null> = computed(
    () => this.$state()?.title ?? null
  );
  protected readonly $bodyContent: Signal<LightboxContentPrimitive | null> = computed(
    () => this.$state()?.body ?? null
  );
  protected readonly $positiveLabel: Signal<string | null> = computed(
    () => (this.$state() as any)?.positiveLabel ?? null
  );
  protected readonly $negativeLabel: Signal<string | null> = computed(
    () => (this.$state() as any)?.negativeLabel ?? null
  );

  constructor() {
    effect(() => {
      const isOpen = this.$state().isOpen;
      const currentVisualState = untracked(this.$visualState);
      if (isOpen) {
        if (currentVisualState === 'closed') {
          this._$visualState.set('opening');
          this._hostElement.nativeElement.showModal();
        } else if (currentVisualState === 'closing') {
          this._$visualState.set('open');
        }
      } else if (currentVisualState === 'open' || currentVisualState === 'opening') {
        // A hidden dialog never fires `animationend`, so finalize the close immediately.
        if (untracked(this._lightboxSvc.$isHidden)) {
          this._$visualState.set('closed');
          this._lightboxSvc.notifyClosed();
        } else {
          this._$visualState.set('closing');
        }
      }
    });
    effect(() => {
      const isHidden = this._lightboxSvc.$isHidden();
      const dialog = this._hostElement.nativeElement;
      if (isHidden && dialog.open) {
        dialog.close();
      } else if (!isHidden && !dialog.open && untracked(this.$visualState) !== 'closed') {
        dialog.showModal();
      }
    });
  }

  ngAfterViewInit(): void {
    this._lightboxSvc.registerHost(this._hostElement.nativeElement);
    this._lightboxSvc.registerOutlet(this._$dynamicOutlet() ?? null);
  }

  ngOnDestroy(): void {
    this._lightboxSvc.registerHost(null);
    this._lightboxSvc.registerOutlet(null);
  }

  protected isTemplateRef(content: LightboxContentPrimitive | null): content is TemplateRef<unknown> {
    return content instanceof TemplateRef;
  }

  protected asTemplateRef(content: LightboxContentPrimitive | null): TemplateRef<unknown> | null {
    return this.isTemplateRef(content) ? content : null;
  }

  protected dismiss(typeOfDismissal: 'optional' | 'forced' = 'optional', isPositiveAction = true): void {
    this._lightboxSvc.dismiss(typeOfDismissal, isPositiveAction);
  }

  protected onCancel(evt: Event): void {
    evt.preventDefault();
    this.dismiss();
  }

  protected onBackdropClick(evt: MouseEvent): void {
    if (evt.target === this._hostElement.nativeElement) {
      this.dismiss();
    }
  }

  protected onAnimationEnd(evt: AnimationEvent): void {
    if (evt.target !== this._hostElement.nativeElement) {
      return;
    }
    if (this.$visualState() === 'opening') {
      this._$visualState.set('open');
    } else if (this.$visualState() === 'closing') {
      this._hostElement.nativeElement.close();
      this._$visualState.set('closed');
      this._lightboxSvc.notifyClosed();
    }
  }
}
